import type { LinkTarget } from '../../../../scripts/lib/link-release.mjs';

type BrowserPlatform = {
  userAgent: string;
  platform: string;
  maxTouchPoints: number;
  userAgentData?: {
    platform: string;
    mobile: boolean;
    getHighEntropyValues?: (
      hints: string[],
    ) => Promise<{ architecture?: string; bitness?: string }>;
  };
};

export type LinkPlatform = {
  kind: 'mac' | 'windows' | 'mobile' | 'unsupported' | 'unknown';
  target: LinkTarget | null;
};

// Browser hints are advisory. In particular, Intel in a Mac user agent does not identify its chip.
export async function detectLinkPlatform(browser?: BrowserPlatform): Promise<LinkPlatform> {
  if (!browser) return { kind: 'unknown', target: null };
  const { userAgent, platform, maxTouchPoints, userAgentData } = browser;
  if (
    userAgentData?.mobile ||
    /Android|iPhone|iPad|iPod|Mobile/i.test(userAgent) ||
    (/Mac/i.test(platform) && maxTouchPoints > 1)
  ) {
    return { kind: 'mobile', target: null };
  }
  const os = userAgentData?.platform || platform || userAgent;
  const kind = /Windows|Win32|Win64/i.test(os)
    ? 'windows'
    : /macOS|MacIntel|MacPPC|Macintosh/i.test(os)
      ? 'mac'
      : /Linux|CrOS|Chrome OS/i.test(os)
        ? 'unsupported'
        : 'unknown';
  if (kind !== 'windows' && kind !== 'mac') return { kind, target: null };

  let hints: { architecture?: string; bitness?: string } = {};
  let timeout: ReturnType<typeof setTimeout> | undefined;
  try {
    if (userAgentData?.getHighEntropyValues) {
      hints = await Promise.race([
        userAgentData.getHighEntropyValues(['architecture', 'bitness']),
        new Promise<{}>((resolve) => {
          timeout = setTimeout(() => resolve({}), 1500);
        }),
      ]);
    }
  } catch {
    // Restricted or unavailable hints must leave manual selection usable.
  } finally {
    clearTimeout(timeout);
  }
  if (hints.architecture === 'arm' && hints.bitness === '64' && kind === 'mac') {
    return { kind, target: 'mac-arm64' };
  }
  if (hints.architecture === 'x86' && hints.bitness === '64') {
    return { kind, target: kind === 'mac' ? 'mac-x64' : 'win-x64' };
  }
  if (
    kind === 'windows' &&
    !hints.architecture &&
    !hints.bitness &&
    /Win64; x64|WOW64/i.test(userAgent) &&
    !/ARM|aarch64/i.test(userAgent)
  ) {
    return { kind, target: 'win-x64' };
  }
  return { kind, target: null };
}
